import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgentControlPlane } from './AgentControlPlane';
import {
  getOwnerId,
  getStoredCapability,
  getStoredTier,
  persistCapability,
  persistTier,
  supabase,
  type AgentCapability,
  type AgentTier,
} from '../../lib/supabase';
import { Track } from '../../lib/types';
import { VIEW_ROUTES, type ViewKey } from '../../lib/view-keys';

interface Stats {
  total: number;
  inReview: number;
  approved: number;
  durationMinutes: number;
}

const MOCK_STATS: Stats = {
  total: 408,
  inReview: 23,
  approved: 347,
  durationMinutes: 2416,
};

interface AgentModeHostProps {
  onTimerStart?: () => void;
}

/**
 * Hosts the Katie Agent Control Plane inside the submission pipeline shell.
 * Loads catalog/agent state from Supabase when configured; falls back to mock stats.
 */
export function AgentModeHost({ onTimerStart }: AgentModeHostProps) {
  const navigate = useNavigate();
  const [ownerId] = useState(() => getOwnerId());
  const [tracks, setTracks] = useState<Track[]>([]);
  const [stats, setStats] = useState<Stats>(MOCK_STATS);
  const [loading, setLoading] = useState(true);
  const [tier, setTier] = useState<AgentTier>(() => getStoredTier());
  const [capability, setCapability] = useState<AgentCapability>(() => getStoredCapability());

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('tracks')
        .select('*')
        .eq('owner_id', ownerId)
        .order('updated_at', { ascending: false });

      if (!error && data && data.length > 0) {
        const list = data as Track[];
        setTracks(list);
        const inReview = list.filter((t) => t.status === 'in_review').length;
        const approved = list.filter((t) => t.status === 'approved' || t.status === 'published').length;
        const durationMinutes = Math.round(
          list.reduce((sum, t) => sum + (Number(t.duration_seconds) || 0), 0) / 60,
        );
        setStats({
          total: list.length,
          inReview,
          approved,
          durationMinutes,
        });
      } else {
        setTracks([]);
        setStats(MOCK_STATS);
      }
    } catch {
      setTracks([]);
      setStats(MOCK_STATS);
    } finally {
      setLoading(false);
    }
  }, [ownerId]);

  useEffect(() => {
    void loadDashboard();
  }, [loadDashboard]);

  const handleNavigate = (view: ViewKey) => {
    navigate(VIEW_ROUTES[view] || '/dashboard');
  };

  const handleSelectTrack = (_id: string) => {
    navigate('/approvals');
  };

  const handleTier = (next: AgentTier) => {
    setTier(next);
    void persistTier(ownerId, next);
  };

  const handleCapability = (next: AgentCapability) => {
    setCapability(next);
    void persistCapability(ownerId, next);
  };

  return (
    <AgentControlPlane
      ownerId={ownerId}
      tracks={tracks}
      stats={stats}
      loading={loading}
      tier={tier}
      capability={capability}
      onTierChange={handleTier}
      onCapabilityChange={handleCapability}
      onNavigate={handleNavigate}
      onSelectTrack={handleSelectTrack}
      onRefresh={() => void loadDashboard()}
      onTimerStart={onTimerStart}
    />
  );
}

export default AgentModeHost;
